import { PaginatedTable, SortByConfig } from "components/Table/Table";
import { IProject, ProjectType } from "interfaces/project";
import { CellProps, Column } from "react-table";
import { styled } from "@mui/material/styles";
import { Box, IconButton, Paper, Tooltip } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useShallow } from "zustand/shallow";

import { TooltipText } from "components/TooltipText";
import {
  CaseAction,
  CaseActionsResponse,
  CaseActionType
} from "interfaces/case-action";
import {
  AvatarCell,
  CaseTableLinkCell,
  DateCell
} from "components/SharedTable/tableRenderers";
import { useContext, useMemo, useState } from "react";
import { makeQueryString } from "core/makeQueryString";
import { useQuery } from "@tanstack/react-query";
import { AxiosContext } from "contexts/AxiosContext";
import { usePagination } from "hooks/table-hooks/usePagination";
import { API_URL_CASE_ACTIONS } from "constants/api_urls";
import { useProjectsStore } from "stores/projects.store";
import { EditCaseActionDialog } from "components/CaseManagement/CaseForms/EditCaseActionDialog";
import { useCaseActionQuery } from "hooks/queries/useCaseActionsQuery";

export interface IProjectActionsTableProps {
  selectedProject: IProject | undefined;
}

const ActionsTableDefaultSort: SortByConfig[] = [
  {
    id: "id",
    desc: false
  }
];

export const ProjectActionsTable = ({
  selectedProject
}: IProjectActionsTableProps): JSX.Element => {
  const { axios } = useContext(AxiosContext)!;
  const {
    setSelectedProjectActionId,
    selectedProjectActionId
  } = useProjectsStore(
    useShallow(state => ({
      setSelectedProjectActionId: state.setSelectedProjectActionId,
      selectedProjectActionId: state.selectedProjectActionId
    }))
  );
  const caseActionQuery = useCaseActionQuery(selectedProjectActionId);

  const [action, setAction] = useState<CaseAction | undefined>(undefined);
  const [showCaseActionDialog, setShowCaseActionDialog] = useState(false);

  const columnDefinitions: Column<CaseAction>[] = [
    {
      Header: "Manage",
      id: "manage_actions",
      accessor: (data: CaseAction) => data.id,
      disableSortBy: true,
      Cell: (data: any) => {
        return (
          <CellContent>
            <Tooltip title="Edit action">
              <IconButton
                onClick={event => {
                  event.stopPropagation();
                  setShowCaseActionDialog(true);
                  setAction(data.cell.row.original);
                }}
              >
                <EditOutlinedIcon />
              </IconButton>
            </Tooltip>
          </CellContent>
        );
      }
    },
    {
      Header: "Action ID",
      id: "id",
      accessor: (data: CaseAction) => data.id,
      Cell: ({ value }: CellProps<CaseAction>) => TooltipText({ text: value })
    },
    {
      Header: "Case ID",
      id: "case_display_id",
      accessor: (data: CaseAction) => data.case_display_id,
      Cell: (data: CellProps<CaseAction>) =>
        CaseTableLinkCell({
          value: data.cell.value,
          caseId: data.cell.row.original.case
        })
    },
    {
      Header: "Action Status",
      id: "action_status",
      accessor: (data: CaseAction) => data.action_status_detail["name"],
      Cell: (cell: CellProps<CaseAction>) => TooltipText({ text: cell.value })
    },
    {
      Header: "Due Date",
      id: "requested_completion_date",
      accessor: (data: CaseAction) => data.requested_completion_date,
      Cell: (data: CellProps<CaseAction>) => DateCell({ value: data.value })
    },
    {
      Header: (
        <Tooltip title="Estimated Completion Date">
          <span>Est. Comp. Date</span>
        </Tooltip>
      ),
      id: "estimated_completion_date",
      accessor: (data: CaseAction) => data.estimated_completion_date,
      Cell: (data: CellProps<CaseAction>) => DateCell({ value: data.value })
    },
    {
      Header: "Assignee",
      id: "assignee",
      accessor: (data: CaseAction) => data.assignee_detail,
      Cell: ({ value }: CellProps<CaseAction>) =>
        AvatarCell({
          firstName: value?.first_name,
          lastName: value?.last_name
        })
    }
  ];

  const {
    pageIndex: actionsPageIndex,
    pageSize: actionsPageSize,
    changePageIndex: actionsChangePageIndex,
    changePageSize: actionsChangePageSize
  } = usePagination();

  const [orderingGetActions, setOrderingGetActions] = useState<SortByConfig[]>(
    ActionsTableDefaultSort
  );

  const projectActionType = useMemo(() => {
    if (!selectedProject) {
      return null;
    }

    switch (selectedProject.project_type) {
      case ProjectType.REACTIVE_INSPECTION:
        return CaseActionType.Inspect;
      case ProjectType.REPAIR:
        return CaseActionType.Repair;
      default:
        return null;
    }
  }, [selectedProject]);

  const actionsQueryQueryString = makeQueryString({
    page: actionsPageIndex + 1,
    size: actionsPageSize,
    case__project: selectedProject?.id,
    action_type: projectActionType,
    ordering: (orderingGetActions.length > 0
      ? orderingGetActions
      : ActionsTableDefaultSort
    )
      .map(ord => `${ord.desc ? "-" : ""}${ord.id}`)
      .join(",")
  });

  const actionsQuery = useQuery({
    queryKey: ["actions", selectedProject, actionsQueryQueryString],
    queryFn: () =>
      axios
        .get<CaseActionsResponse>(
          `${API_URL_CASE_ACTIONS}${actionsQueryQueryString}`
        )
        .then(r => r.data),
    cacheTime: 0,
    refetchOnWindowFocus: false,
    enabled: selectedProject !== undefined,
    onSuccess: data => {
      if (actionsQuery.isInitialLoading) {
        setSelectedProjectActionId(data.results[0]?.id);
      }
    }
  });

  return (
    <>
      <TableContainer>
        <PaginatedTable
          data={actionsQuery.data?.results || []}
          onClickRow={row => setSelectedProjectActionId(row.values.id)}
          isRowSelected={row => row.id === selectedProjectActionId}
          columns={columnDefinitions}
          loading={actionsQuery.isLoading}
          dataQa="PP-Actions-Table"
          count={actionsQuery.data?.count || 0}
          page={(actionsQuery?.data?.count || 0) > 0 ? actionsPageIndex : 0}
          pageSize={actionsPageSize}
          onPageChange={(_event, p: number) => actionsChangePageIndex(p)}
          onPageSizeChange={(
            event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
          ) => actionsChangePageSize(parseInt(event.target.value))}
          onSortingChange={sorting =>
            setOrderingGetActions(
              sorting.map(s => ({
                id: s.id,
                desc: s.desc || false
              }))
            )
          }
          defaultSortBy={ActionsTableDefaultSort}
        />
      </TableContainer>

      <EditCaseActionDialog
        open={showCaseActionDialog}
        setOpen={setShowCaseActionDialog}
        action={action}
        caseId={action?.case}
        onReplaceExistingAction={() => {
          actionsQuery.refetch();
          caseActionQuery.refetch();
        }}
      />
    </>
  );
};

const TableContainer = styled(Paper)`
  display: flex;
  flex-direction: column;
  height: 100%;
  border: 1px solid rgba(224, 224, 224, 1);
  border-radius: 4px;
  box-shadow: none;
`;
TableContainer.displayName = "TableContainer";

const CellContent = styled(Box)`
  display: flex;
  align-items: center;
  gap: 10px;
`;
CellContent.displayName = "CellContent";
